import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, IconButton, Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ZicassoChat,
  BackNavigationAppBar,
  OutlinedIcons,
} from '@marageti/z4-lib';
import {
  Conversation,
  Message,
  GetParticipantsResponse,
  TripMatch,
  Partner,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import AddDocumentsDialog from '../../../../components/add-documents-dialog';
import MobileAlertDialog from '../../../../components/legacy-trip-request-dialog';
import Sidebar from '../../sidebar';

type Props = {
  conversationTitle?: string,
  conversation: Conversation | undefined,
  handleViewClick: (message: Message) => void,
  isError: boolean,
  isLoading: boolean,
  isValidating: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  participants: GetParticipantsResponse | undefined,
  sendMessage: (message: string) => void,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  updateMessages: () => void,
  tripMatch: TripMatch | undefined,
  partner?: Partner | undefined,
  isLegacyTR?: boolean,
};

const MessagesView = ({
  conversationTitle = undefined,
  conversation,
  handleViewClick,
  isError,
  isLoading,
  isValidating,
  messages,
  lastMessageSeenId,
  loadMoreMessages,
  hasMoreMessages,
  participants,
  sendMessage,
  viewer,
  updateMessages,
  tripMatch,
  partner,
  isLegacyTR = false,
}: Props) => {
  const [showAddDocuments, setShowAddDocuments] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const navigate = useNavigate();
  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Go back to where the user came from when there's in-app history; fall back to
  // the trip request on a direct load / post-login redirect so we never land on auth.
  const handleBack = useCallback(() => {
    if (location.key === 'default') {
      const targetPath = tripMatch?.tripRequestId
        ? `/trip-requests/${tripMatch.tripRequestId}`
        : '/';
      navigate(targetPath, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, location.key, tripMatch?.tripRequestId]);

  const {
    OutlinedInformationCircleIcon,
  } = OutlinedIcons;

  return (
    <>
      <Container
        maxWidth="xl"
        sx={{
          ...(isMobile ? {
            position: 'fixed',
            inset: 0,
            backgroundColor: 'primary.contrastText',
            zIndex: 1200,
            padding: 0,
          } : {
            display: 'flex',
            flex: 1,
            pt: 3.75,
          }),
        }}
      >
        {/* Mobile Header */}
        {isMobile && (
        <>
          {isLegacyTR && <MobileAlertDialog open={dialogOpen} onClose={handleDialogClose} />}
          <BackNavigationAppBar
            primary={conversationTitle || <Skeleton width={150} />}
            onClick={handleBack}
            endAdornment={isLegacyTR ? (
              <IconButton onClick={handleDialogOpen}>
                <OutlinedInformationCircleIcon color="primary" />
              </IconButton>
            ) : null}
          />
        </>
        )}

        <Grid
          container
          sx={{
            px: {
              xs: 3,
              sm: 0,
            },
          }}
          spacing={isMobile ? 3 : 4}
        >
          {/* Sticky Sidebar */}
          {!isMobile && (
          <Grid
            item
            desktop={4}
            sx={{
              display: 'block',
              borderRight: '1px solid #C2C2C2',
            }}
          >
            {/* Sticky Sidebar */}
            <Sidebar isMessagingPage partner={partner} isLegacyTR={isLegacyTR} />
          </Grid>
          )}

          {/* Main Content */}
          <Grid
            item
            desktop={isMobile ? 12 : 8}
            sx={{
              ...(isMobile && {
                display: 'flex',
                flex: 1,
                minHeight: 'calc(100vh - 72px)',
              }),
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              overflow: 'hidden',
              maxWidth: '100vw',
              height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100vh - 198px)',
            }}
            >
              <ZicassoChat
                hideToolbarOnMobile
                isError={isError}
                isLoading={isLoading}
                isValidating={isValidating}
                conversation={conversation}
                messages={messages}
                lastMessageSeenId={lastMessageSeenId}
                loadPreviousPages={hasMoreMessages}
                onLoadPreviousPage={loadMoreMessages}
                onSend={sendMessage}
                onViewClick={handleViewClick}
                participants={participants}
                viewer={viewer}
                contextMenuItems={[
                  {
                    icon: <OutlinedIcons.OutlinedUploadIcon color="primary" />,
                    action: () => setShowAddDocuments(true),
                    label: 'Upload Document',
                  },
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      <AddDocumentsDialog
        closeDialog={() => setShowAddDocuments(false)}
        open={showAddDocuments}
        onDocumentUploaded={updateMessages}
      />
    </>
  );
};

export default MessagesView;
